import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Handshake, Loader2, MessageCircle, Pencil, Sparkles, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { openRidePayment } from "@/components/price/ScheduledRidesSheet";
import { ProposePriceButton, ProposePriceSheet, eur } from "@/components/price/ProposePriceSheet";

const ST = { open: "En négociation", accepted: "Prix convenu", refused: "Refusée", closed: "Close" };

// Bouton + panneau de saisie du prix, réutilisé partout.
function ProposeAction({ label, sheetTitle, subtitle, recommended, initial, unit, testid, onSubmit, variant }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <ProposePriceButton label={label} onClick={() => setOpen(true)} testid={`${testid}-open`} variant={variant} />
      <ProposePriceSheet open={open} onClose={() => setOpen(false)} title={sheetTitle || label} subtitle={subtitle}
        recommended={recommended} initial={initial} unit={unit} onSubmit={onSubmit} testid={testid} />
    </>
  );
}

// Fil d'une négociation : historique + actions (accepter / contre-proposer / refuser).
export function OfferThread({ offer, me, onChange }) {
  const unit = offer.kind === "seat" ? "€/place" : "€";
  const act = async (fn, ok) => {
    try {
      const { data } = await fn();
      if (ok) toast.success(ok);
      const rid = data?.request_id || data?.request?.id;
      if (data?.payment_required && rid && me === "passenger") await openRidePayment(rid, onChange);
      onChange?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const who = (by) => (by === me ? "Toi" : by === "driver" ? (offer.driver?.name || "Covoitureur") : (offer.client_name || "Passager"));
  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin" data-testid={`offer-${offer.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[13px] font-black">{me === "passenger" ? offer.driver?.name : offer.client_name} · {offer.to_address}</div>
          <div className="text-[11px] font-semibold text-foreground/60">
            {offer.driver?.vehicle && me === "passenger" ? `${offer.driver.vehicle} · ` : ""}{offer.seats} place(s)
            {offer.recommended_eur ? ` · prix recommandé ${eur(offer.recommended_eur)}${offer.kind === "seat" ? "/place" : ""}` : ""}
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-[#FFE9D6] px-2 py-0.5 text-[10px] font-black" data-testid={`offer-status-${offer.id}`}>{ST[offer.status]}</span>
      </div>
      <div className="mt-2 space-y-1">
        {offer.rounds.map((r, i) => (
          <div key={i} className={`flex justify-between rounded-lg px-2 py-1 text-[12px] font-bold ${r.by === me ? "bg-primary/10" : "bg-secondary/50"}`} data-testid={`offer-round-${offer.id}-${i}`}>
            <span>{who(r.by)}</span><span className="font-[Outfit] font-black">{eur(r.amount)}{offer.kind === "seat" ? "/place" : ""}</span>
          </div>
        ))}
      </div>
      {offer.status === "accepted" && (
        <div className="mt-2 flex items-center gap-1.5 text-[12px] font-black text-emerald-700" data-testid={`offer-agreed-${offer.id}`}>
          <Handshake className="h-4 w-4" />Prix convenu : {eur(offer.agreed_eur ?? offer.amount)}{offer.kind === "seat" ? "/place — tu peux réserver" : ""}
        </div>
      )}
      {offer.my_turn && (
        <>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => act(() => api.post(`/price/offers/${offer.id}/accept`), "Prix accepté 🤝")}
              className="street-press flex items-center justify-center gap-1 rounded-xl bg-emerald-500 py-2 text-[13px] font-black text-white street-border-thin" data-testid={`offer-accept-${offer.id}`}>
              <Check className="h-4 w-4" />Accepter {eur(offer.amount)}
            </button>
            <button type="button" onClick={() => act(() => api.post(`/price/offers/${offer.id}/refuse`), "Proposition refusée")}
              className="flex items-center justify-center gap-1 rounded-xl bg-white py-2 text-[13px] font-black text-red-600 street-border-thin" data-testid={`offer-refuse-${offer.id}`}>
              <X className="h-4 w-4" />Refuser
            </button>
          </div>
          {offer.can_counter && (
            <div className="mt-2">
              <ProposeAction label="Proposer un autre prix" sheetTitle="Ta contre-proposition" variant="secondary"
                subtitle={`Dernière proposition : ${eur(offer.amount)} · ${offer.rounds_left} échange(s) restant(s)`}
                recommended={offer.recommended_eur} initial={offer.amount} unit={unit} testid={`offer-counter-${offer.id}`}
                onSubmit={(n) => act(() => api.post(`/price/offers/${offer.id}/counter`, { amount: n }), "Contre-proposition envoyée")} />
            </div>
          )}
          <p className="mt-1 text-[10px] font-semibold text-foreground/50" data-testid={`offer-rounds-left-${offer.id}`}>{offer.rounds_left} échange(s) restant(s)</p>
        </>
      )}
      {!offer.my_turn && offer.status === "open" && <p className="mt-2 text-[11px] font-bold text-foreground/60">En attente de réponse…</p>}
    </div>
  );
}

// Passager (création de demande) : choix libre du prix, recommandé par défaut.
export function PassengerPriceChooser({ recommended, value, onChange }) {
  const custom = value != null && Math.abs(value - recommended) > 0.004;
  return (
    <div className="mt-3 rounded-2xl bg-[#FFF8F1] p-3 street-border-thin" data-testid="passenger-price-chooser">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-accent px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-accent-foreground street-border-thin"><Sparkles className="h-3 w-3" />Prix libre</span>
        {custom && <button type="button" onClick={() => onChange(null)} className="whitespace-nowrap text-[11px] font-black text-primary underline" data-testid="passenger-price-reset">Revenir au recommandé</button>}
      </div>
      <div className="mt-2 flex items-baseline justify-between gap-2">
        <span className="text-[12px] font-bold text-foreground/70">{custom ? "Ton prix proposé" : "Prix recommandé Price"}</span>
        <span className="font-[Outfit] text-[24px] font-black tabular-nums" data-testid="passenger-price-value">{eur(custom ? value : recommended)}</span>
      </div>
      {custom && <p className="text-[11px] font-semibold text-foreground/60">Recommandé : {eur(recommended)}</p>}
      <p className="mt-1 text-[11px] font-semibold text-foreground/60">Les covoitureurs peuvent accepter ton prix ou en proposer un autre (3 échanges max).</p>
      <div className="mt-2">
        <ProposeAction label={custom ? "Modifier mon prix" : "Proposer mon prix"} sheetTitle="Propose ton prix" subtitle="Ce prix sera affiché aux covoitureurs."
          recommended={recommended} initial={custom ? value : recommended} testid="passenger-price" onSubmit={async (n) => onChange(n)} />
      </div>
    </div>
  );
}

// Conducteur : accepter le prix du passager ou proposer le sien.
export function DriverOfferBox({ req, onSent }) {
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/price/offers/mine").then(({ data }) => setMine(data.items.find((o) => o.target_id === req.id) || false)).catch(() => setMine(false)), [req.id]);
  useEffect(() => { load(); }, [load]);
  if (mine === null) return null;
  if (mine) return <div className="mt-3"><OfferThread offer={mine} me="driver" onChange={load} /></div>;
  const reco = req.recommended_price_eur ?? req.price_eur;
  const proposed = req.passenger_proposed_eur;
  const acceptProposal = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/price/ride-requests/${req.id}/accept-proposal`); toast.success("Prix du passager accepté 🤝"); load(); onSent?.(data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 rounded-2xl bg-secondary/40 p-3 street-border-thin" data-testid={`driver-offer-box-${req.id}`}>
      <div className="flex items-center gap-1.5 text-[13px] font-black"><MessageCircle className="h-4 w-4" />Longue distance · prix libre</div>
      <div className="text-[11px] font-semibold text-foreground/60">Prix recommandé par Price : {eur(reco)}</div>
      {proposed ? (
        <div className="mt-2 flex items-center justify-between rounded-xl bg-white px-3 py-2 street-border-thin" data-testid={`driver-passenger-proposal-${req.id}`}>
          <span className="text-[12px] font-black">Le passager propose</span>
          <span className="font-[Outfit] text-[20px] font-black text-primary tabular-nums">{eur(proposed)}</span>
        </div>
      ) : null}
      <div className="mt-2 space-y-2">
        {proposed ? (
          <button type="button" disabled={busy} onClick={acceptProposal} className="street-press flex w-full items-center justify-center gap-1.5 rounded-2xl bg-emerald-500 py-3 font-[Outfit] text-[14px] font-black text-white street-border-thin disabled:opacity-60" data-testid={`driver-accept-proposal-${req.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" />Accepter {eur(proposed)}</>}
          </button>
        ) : null}
        <ProposeAction label={proposed ? "Proposer un autre prix" : "Proposer mon prix"} sheetTitle="Propose ton prix" variant={proposed ? "secondary" : "primary"}
          subtitle={proposed ? `Le passager propose ${eur(proposed)}.` : "Le passager pourra accepter, refuser ou contre-proposer."}
          recommended={reco} initial={proposed || reco} testid={`driver-offer-${req.id}`}
          onSubmit={async (n) => { await api.post(`/price/ride-requests/${req.id}/offers`, { amount: n }); toast.success("Proposition envoyée au passager"); load(); onSent?.(); }} />
      </div>
    </div>
  );
}

// Passager : son prix proposé + propositions reçues sur sa demande longue distance.
export function RequestOffersPanel({ requestId, recommended, onAgreed }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/price/ride-requests/${requestId}/offers`).then(({ data: d }) => setData(d)).catch(() => setData({ items: [] })), [requestId]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  const items = data?.items;
  const proposed = data?.passenger_proposed_eur;
  const agreed = items?.some((o) => o.status === "accepted");
  const toPay = data?.request_status === "pending_payment" && agreed;
  return (
    <div className="space-y-2" data-testid="request-offers-panel">
      {toPay && (
        <button type="button" onClick={() => openRidePayment(requestId, load)} className="street-press flex w-full items-center justify-center gap-1.5 rounded-2xl bg-primary py-3.5 font-[Outfit] text-[15px] font-black text-primary-foreground street-border street-shadow" data-testid="request-pay-agreed">
          <Handshake className="h-4 w-4" />Prix accepté : payer {eur(data.agreed_eur)}
        </button>
      )}
      {!agreed && data && (
        <div className="rounded-2xl bg-white p-3 street-border-thin" data-testid="request-my-price">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[12px] font-black">{proposed ? "Ton prix proposé" : "Prix recommandé Price"}</span>
            <span className="font-[Outfit] text-[22px] font-black text-primary tabular-nums" data-testid="request-my-price-value">{eur(proposed || recommended)}</span>
          </div>
          {proposed ? <p className="text-[11px] font-semibold text-foreground/60">Recommandé : {eur(recommended)}</p> : null}
          <div className="mt-2">
            <ProposeAction label={proposed ? "Modifier mon prix" : "Proposer mon prix"} sheetTitle="Propose ton prix" variant="secondary"
              subtitle="Les nouveaux covoitureurs verront ce prix. Les négociations déjà ouvertes continuent."
              recommended={recommended} initial={proposed || recommended} testid="request-propose"
              onSubmit={async (n) => { await api.put(`/price/ride-requests/${requestId}/proposed-price`, { amount: n }); toast.success("Ton prix a été mis à jour"); load(); }} />
          </div>
        </div>
      )}
      <div className="text-[13px] font-black">Propositions des covoitureurs</div>
      <p className="text-[11px] font-semibold text-foreground/60">Longue distance : les covoitureurs acceptent ton prix ou proposent le leur (3 échanges max).</p>
      {items === undefined ? <Loader2 className="h-5 w-5 animate-spin" /> : items.length === 0
        ? <p className="rounded-xl bg-white p-3 text-[12px] font-semibold street-border-thin" data-testid="request-offers-empty">Aucune proposition pour l&apos;instant…</p>
        : items.map((o) => <OfferThread key={o.id} offer={o} me="passenger" onChange={(d) => { load(); if (d?.kind === "request" && !d?.payment_required) onAgreed?.(d); }} />)}
    </div>
  );
}

// Passager : proposer un prix par place sur un trajet publié longue distance.
export function TripOfferBox({ trip, seats }) {
  const [offer, setOffer] = useState(undefined);
  const load = useCallback(() => api.get(`/price/trips/${trip.id}/my-offer`).then(({ data }) => setOffer(data.offer)).catch(() => setOffer(null)), [trip.id]);
  useEffect(() => { load(); }, [load]);
  if (offer === undefined) return null;
  if (offer) return <div className="mt-2"><OfferThread offer={offer} me="passenger" onChange={load} /></div>;
  return (
    <div className="mt-2 rounded-2xl bg-secondary/40 p-3 street-border-thin" data-testid={`trip-offer-box-${trip.id}`}>
      <div className="flex items-center gap-1.5 text-[12px] font-black"><Pencil className="h-3.5 w-3.5" />Prix libre : propose ton prix ({seats} place(s))</div>
      {trip.recommended_price_per_seat_eur && <div className="text-[11px] font-semibold text-foreground/60">Prix recommandé par Price : {eur(trip.recommended_price_per_seat_eur)}/place</div>}
      <div className="mt-2">
        <ProposeAction label="Proposer mon prix" sheetTitle="Propose ton prix par place" variant="secondary"
          subtitle={`Prix affiché par le covoitureur : ${eur(trip.price_per_seat_eur)}/place`}
          recommended={trip.recommended_price_per_seat_eur} initial={trip.price_per_seat_eur} unit="€/place" testid={`trip-offer-${trip.id}`}
          onSubmit={async (n) => { await api.post(`/price/trips/${trip.id}/offers`, { amount: n, seats }); toast.success("Proposition envoyée au covoitureur"); load(); }} />
      </div>
    </div>
  );
}

// Liste des négociations en cours (conducteur ou passager).
export function MyOffersPanel({ me }) {
  const [items, setItems] = useState([]);
  // Conducteur : les demandes sont déjà négociées dans leur carte, ici seulement les trajets publiés.
  const load = useCallback(() => api.get("/price/offers/mine").then(({ data }) => setItems(data.items.filter((o) => o.me === me && o.status === "open" && (me !== "driver" || o.kind === "seat")))).catch(() => {}), [me]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  if (!items.length) return null;
  return (
    <div className="space-y-2" data-testid={`my-offers-panel-${me}`}>
      <div className="font-[Outfit] text-[15px] font-black">Négociations en cours</div>
      {items.map((o) => <OfferThread key={o.id} offer={o} me={me} onChange={load} />)}
    </div>
  );
}
